import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  HeartHandshake, DollarSign, Clock, CheckCircle, 
  XCircle, AlertCircle, Pen, FileText, Loader2,
  ShieldCheck, FileSignature, Scale, Lock, UserX
} from 'lucide-react';
import { offerLettersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const OfferLetter = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [showAgreementDetail, setShowAgreementDetail] = useState(null);
  
  // Agreement checkboxes
  const [agreements, setAgreements] = useState({
    contractor_agreement: false,
    background_check_consent: false,
    liability_waiver: false,
    confidentiality_agreement: false,
    non_solicitation_agreement: false
  });

  useEffect(() => {
    fetchOffer();
  }, [offerId]);

  const fetchOffer = async () => {
    try {
      const response = await offerLettersAPI.getPublic(offerId);
      setOffer(response.data);
    } catch (error) {
      console.error('Failed to fetch offer:', error);
      toast.error('Offer letter not found or has been removed');
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing functions
  useEffect(() => {
    if (showSignatureModal && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#374151';
    }
  }, [showSignatureModal]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const allAgreementsAccepted = Object.values(agreements).every(v => v);

  const handleSign = async () => {
    // Check all agreements are accepted
    if (!allAgreementsAccepted) {
      toast.error('You must accept all agreements to proceed');
      return;
    }
    
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    
    // Check if canvas is empty (just white)
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const isBlank = [...imageData.data].every((val, idx) => idx % 4 === 3 || val === 255);
    
    if (isBlank) {
      toast.error('Please draw your signature before signing');
      return;
    }

    setSigning(true);
    try {
      const response = await offerLettersAPI.signPublic(offerId, {
        ...agreements,
        signature_data: signatureData
      });
      
      // Handle both new and existing account scenarios
      if (response.data.existing_account) {
        setCredentials({
          email: response.data.email,
          existingAccount: true
        });
        toast.success('Offer letter signed! Login with your existing password.');
      } else {
        setCredentials({
          email: response.data.email,
          password: response.data.temp_password
        });
        toast.success('Offer letter signed! Check your email for login credentials.');
      }
      
      setShowSignatureModal(false);
      fetchOffer(); // Refresh offer status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign offer letter');
    } finally {
      setSigning(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diff < 0) return 'Expired';
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
    return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="animate-pulse-soft text-[var(--primary)] text-lg flex items-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading offer letter...
        </div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[var(--background)] p-4">
        <XCircle className="w-20 h-20 text-red-400 mb-6" />
        <h1 className="text-2xl font-bold mb-2">Offer Not Found</h1>
        <p className="text-[var(--text-muted)] text-center mb-6">
          This offer letter link is invalid or has been removed.
        </p>
        <Link to="/" className="btn-primary">
          Return Home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Header */}
      <header className="nav-glass sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <HeartHandshake className="w-8 h-8 text-[var(--primary)]" />
            <span className="text-xl font-bold gradient-text font-['Cormorant_Garamond']">NC Help Hub</span>
          </Link>
          <span className="badge-primary">Offer Letter</span>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Success State - Show Credentials */}
        {credentials && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-8 mb-8 border-2 border-[var(--success)]"
            data-testid="credentials-card"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[var(--success)] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--success)]">Welcome to NC Help Hub!</h2>
              <p className="text-[var(--text-secondary)]">
                {credentials.existingAccount 
                  ? 'Your offer letter has been signed' 
                  : 'Your account has been created'}
              </p>
            </div>
            
            <div className="bg-[var(--background-subtle)] p-6 rounded-xl mb-6">
              <h3 className="font-semibold mb-4">
                {credentials.existingAccount ? 'Login Information' : 'Your Login Credentials'}
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-[var(--background-paper)] rounded-lg">
                  <span className="text-[var(--text-muted)]">Email:</span>
                  <span className="font-mono font-semibold">{credentials.email}</span>
                </div>
                {credentials.existingAccount ? (
                  <div className="p-3 bg-[#D4AF37]/10 rounded-lg">
                    <p className="text-blue-800 text-sm">
                      Use your existing password to login. If you've forgotten it, click "Forgot Password" on the login page.
                    </p>
                  </div>
                ) : (
                  <div className="flex justify-between items-center p-3 bg-[var(--background-paper)] rounded-lg">
                    <span className="text-[var(--text-muted)]">Temporary Password:</span>
                    <span className="font-mono font-semibold bg-[var(--warning)]/15 px-2 py-1 rounded">{credentials.password}</span>
                  </div>
                )}
              </div>
              {!credentials.existingAccount && (
                <p className="text-sm text-amber-600 mt-4 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Please save this password and change it after your first login.
                </p>
              )}
            </div>
            
            <Button 
              onClick={() => navigate('/login')} 
              className="btn-primary w-full"
              data-testid="login-now-btn"
            >
              Login Now
            </Button>
          </motion.div>
        )}

        {/* Offer Status Banner */}
        {offer.status !== 'pending' && !credentials && (
          <div className={`p-4 rounded-xl mb-6 flex items-center gap-3 ${
            offer.status === 'accepted' ? 'bg-[var(--success)]/15 text-[var(--success)]' :
            offer.status === 'rejected' ? 'bg-[var(--error)]/15 text-[var(--error)]' :
            'bg-amber-100 text-amber-800'
          }`}>
            {offer.status === 'accepted' && <CheckCircle className="w-5 h-5" />}
            {offer.status === 'rejected' && <XCircle className="w-5 h-5" />}
            {offer.status === 'expired' && <Clock className="w-5 h-5" />}
            <span className="font-medium capitalize">
              {offer.status === 'accepted' && 'This offer has been accepted'}
              {offer.status === 'rejected' && 'This offer has been declined'}
              {offer.status === 'expired' && 'This offer has expired'}
            </span>
          </div>
        )}

        {/* Offer Letter Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card overflow-hidden"
          data-testid="offer-letter-card"
        >
          {/* Letterhead */}
          <div className="bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] p-8 text-white text-center">
            <HeartHandshake className="w-16 h-16 mx-auto mb-4 opacity-90" />
            <h1 className="text-3xl font-bold font-['Cormorant_Garamond'] mb-2">NC Help Hub</h1>
            <p className="opacity-90">Official Offer of Employment</p>
          </div>

          {/* Letter Content */}
          <div className="p-8">
            <div className="text-right mb-6 text-sm text-[var(--text-muted)]">
              <p>Offer ID: {offer.id}</p>
              <p>Date: {formatDate(offer.created_at)}</p>
            </div>

            <div className="mb-8">
              <p className="mb-4">Dear <strong>{offer.worker_name}</strong>,</p>
              
              <p className="text-[var(--text-secondary)] leading-relaxed mb-4">
                We are pleased to extend this official offer of employment to join the NC Help Hub team 
                as a <strong>{offer.position}</strong>.
              </p>
              
              <p className="text-[var(--text-secondary)] leading-relaxed">
                After reviewing your application, we believe your skills and experience make you an 
                excellent addition to our team. We are excited about the possibility of working together 
                to provide exceptional services to our clients.
              </p>
            </div>

            {/* Compensation Section */}
            <div className="bg-gradient-to-br from-[var(--accent)] to-[var(--background-subtle)] p-6 rounded-2xl mb-8">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[var(--primary)]" />
                Compensation Details
              </h2>
              
              <div className="bg-[var(--background-paper)] p-6 rounded-xl text-center mb-4">
                <p className="text-sm text-[var(--text-muted)] mb-1">Hourly Rate</p>
                <p className="text-4xl font-bold text-[var(--primary)]">${offer.hourly_rate?.toFixed(2)}/hr</p>
              </div>

              {offer.service_types && offer.service_types.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-[var(--text-muted)] mb-2">Service Types:</p>
                  <div className="flex gap-2 flex-wrap">
                    {offer.service_types.map(type => (
                      <span key={type} className="badge-primary">{type.toUpperCase()}</span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-semibold mb-2">Position Benefits Include:</p>
                <ul className="text-sm text-[var(--text-secondary)] space-y-1">
                  <li>• Flexible scheduling based on your availability</li>
                  <li>• Direct deposit payments every pay period</li>
                  <li>• Professional development opportunities</li>
                  <li>• Supportive team environment</li>
                </ul>
              </div>
            </div>

            {/* Expiration Warning */}
            {offer.status === 'pending' && (
              <div className="bg-[var(--warning)]/10 border border-[var(--warning)]/30 p-4 rounded-xl mb-8">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800">Offer Expiration</p>
                    <p className="text-amber-700 text-sm">
                      This offer expires on <strong>{formatDate(offer.expires_at)}</strong>
                    </p>
                    <p className="text-amber-600 text-sm mt-1">
                      {getTimeRemaining(offer.expires_at)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Terms */}
            <div className="mb-8">
              <h3 className="font-semibold mb-3">Employment Classification</h3>
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 rounded-xl mb-4">
                <p className="text-sm text-blue-800 font-medium flex items-center gap-2">
                  <Scale className="w-4 h-4" />
                  W-2 Employee Status
                </p>
                <p className="text-sm text-[#D4AF37] mt-2">
                  You will be classified as a W-2 employee. Taxes will be withheld from your paychecks 
                  based on your W-4 form. You'll receive a W-2 at year end for tax filing.
                </p>
              </div>
            </div>

            {/* Required Agreements - Only show if pending */}
            {offer.status === 'pending' && !offer.is_expired && (
              <div className="mb-8">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <FileSignature className="w-5 h-5 text-[var(--primary)]" />
                  Required Agreements
                </h3>
                <p className="text-sm text-[var(--text-muted)] mb-4">
                  You must review and accept all agreements below to proceed.
                </p>
                
                <div className="space-y-3">
                  {/* Employment Agreement */}
                  <div className="border border-[var(--border-primary)] rounded-xl p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreements.contractor_agreement}
                        onChange={(e) => setAgreements({...agreements, contractor_agreement: e.target.checked})}
                        className="w-5 h-5 mt-0.5 rounded border-[var(--primary)] text-[var(--primary)]"
                        data-testid="agreement-contractor"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Scale className="w-4 h-4 text-[var(--primary)]" />
                          <span className="font-semibold">Employment Agreement</span>
                        </div>
                        <p className="text-sm text-[var(--text-muted)]">
                          I acknowledge that I will be employed as a W-2 employee. Taxes will be withheld 
                          from my paychecks and I will complete the required W-4 form.
                        </p>
                        <button 
                          type="button"
                          onClick={() => setShowAgreementDetail('contractor')}
                          className="text-sm text-[var(--primary)] underline mt-1"
                        >
                          View full agreement
                        </button>
                      </div>
                    </label>
                  </div>

                  {/* Background Check Consent */}
                  <div className="border border-[var(--border-primary)] rounded-xl p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreements.background_check_consent}
                        onChange={(e) => setAgreements({...agreements, background_check_consent: e.target.checked})}
                        className="w-5 h-5 mt-0.5 rounded border-[var(--primary)] text-[var(--primary)]"
                        data-testid="agreement-background"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <ShieldCheck className="w-4 h-4 text-[var(--primary)]" />
                          <span className="font-semibold">Background Check Consent</span>
                        </div>
                        <p className="text-sm text-[var(--text-muted)]">
                          I authorize NC Help Hub to conduct background checks including criminal history 
                          and reference verification.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Liability Waiver */}
                  <div className="border border-[var(--border-primary)] rounded-xl p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreements.liability_waiver}
                        onChange={(e) => setAgreements({...agreements, liability_waiver: e.target.checked})}
                        className="w-5 h-5 mt-0.5 rounded border-[var(--primary)] text-[var(--primary)]"
                        data-testid="agreement-liability"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-4 h-4 text-[var(--primary)]" />
                          <span className="font-semibold">Liability Waiver</span>
                        </div>
                        <p className="text-sm text-[var(--text-muted)]">
                          I understand and accept the risks associated with performing these services.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Confidentiality Agreement */}
                  <div className="border border-[var(--border-primary)] rounded-xl p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreements.confidentiality_agreement}
                        onChange={(e) => setAgreements({...agreements, confidentiality_agreement: e.target.checked})}
                        className="w-5 h-5 mt-0.5 rounded border-[var(--primary)] text-[var(--primary)]"
                        data-testid="agreement-confidentiality"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Lock className="w-4 h-4 text-[var(--primary)]" />
                          <span className="font-semibold">Confidentiality Agreement</span>
                        </div>
                        <p className="text-sm text-[var(--text-muted)]">
                          I agree to maintain strict confidentiality of all client information and company data.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Non-Solicitation Agreement */}
                  <div className="border-2 border-amber-300 bg-[var(--warning)]/10 rounded-xl p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreements.non_solicitation_agreement}
                        onChange={(e) => setAgreements({...agreements, non_solicitation_agreement: e.target.checked})}
                        className="w-5 h-5 mt-0.5 rounded border-[var(--primary)] text-[var(--primary)]"
                        data-testid="agreement-non-solicitation"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <UserX className="w-4 h-4 text-amber-600" />
                          <span className="font-semibold text-amber-800">Non-Solicitation Agreement</span>
                          <span className="badge-warning text-xs">Important</span>
                        </div>
                        <p className="text-sm text-amber-700">
                          I agree NOT to work directly with NC Help Hub customers outside of this platform. 
                          I will not solicit customers or other workers for private arrangements. 
                          Violation may result in termination and legal action.
                        </p>
                        <button 
                          type="button"
                          onClick={() => setShowAgreementDetail('non-solicitation')}
                          className="text-sm text-amber-800 underline mt-1 font-medium"
                        >
                          View full non-solicitation terms
                        </button>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Agreement Status */}
                <div className={`mt-4 p-3 rounded-lg ${allAgreementsAccepted ? 'bg-[var(--success)]/10 border border-[var(--success)]/30' : 'bg-[var(--background-subtle)] border border-[var(--border-primary)]'}`}>
                  <p className={`text-sm font-medium ${allAgreementsAccepted ? 'text-green-700' : 'text-[var(--text-secondary)]'}`}>
                    {allAgreementsAccepted 
                      ? '✓ All agreements accepted - You can now sign the offer letter'
                      : `${Object.values(agreements).filter(v => v).length}/5 agreements accepted`
                    }
                  </p>
                </div>
              </div>
            )}

            {/* Signature Section */}
            <div className="border-t border-[var(--border-primary)] pt-6">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div className="flex-1">
                  <p className="text-sm text-[var(--text-muted)] mb-1">NC Help Hub Management</p>
                  <p className="font-bold">NC Help Hub HR Team</p>
                </div>
                
                {offer.status === 'pending' && !offer.is_expired && (
                  <div className="flex gap-3">
                    <Button 
                      onClick={() => setShowSignatureModal(true)}
                      className="btn-primary"
                      disabled={!allAgreementsAccepted}
                      data-testid="sign-offer-btn"
                    >
                      <Pen className="w-4 h-4 mr-2" />
                      Sign & Accept Offer
                    </Button>
                  </div>
                )}
                
                {offer.status === 'accepted' && (
                  <div className="text-center">
                    <p className="text-sm text-[var(--text-muted)] mb-1">Signed by</p>
                    <p className="font-bold text-[var(--success)]">{offer.worker_name}</p>
                    <p className="text-xs text-[var(--text-muted)]">{formatDate(offer.signed_at)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Login Prompt for Accepted Offers */}
        {offer.status === 'accepted' && !credentials && (
          <div className="mt-6 p-4 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl text-center">
            <p className="text-blue-800 mb-3">
              Already signed? Check your email for login credentials or
            </p>
            <Link to="/login" className="btn-primary inline-block">
              Login to Dashboard
            </Link>
          </div>
        )}
      </main>

      {/* Signature Modal */}
      {showSignatureModal && (
        <div className="modal-overlay" onClick={() => setShowSignatureModal(false)}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal-content max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-[var(--accent)] rounded-full flex items-center justify-center mx-auto mb-3">
                <Pen className="w-6 h-6 text-[var(--primary)]" />
              </div>
              <h2 className="text-xl font-bold">Sign Offer Letter</h2>
              <p className="text-sm text-[var(--text-muted)]">
                Draw your signature below to accept this offer
              </p>
            </div>

            {/* Signature Canvas */}
            <div className="mb-4">
              <div className="border-2 border-dashed border-[var(--border-primary)] rounded-xl overflow-hidden bg-[var(--background-paper)]">
                <canvas
                  ref={canvasRef}
                  width={400}
                  height={200}
                  className="w-full touch-none cursor-crosshair"
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="signature-canvas"
                />
              </div>
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-[var(--text-muted)]">Draw signature with mouse or finger</p>
                <Button variant="ghost" size="sm" onClick={clearSignature}>
                  Clear
                </Button>
              </div>
            </div>

            {/* Confirmation Text */}
            <div className="bg-[var(--background-subtle)] p-4 rounded-xl mb-6 text-sm">
              <p className="text-[var(--text-secondary)]">
                By signing, I <strong>{offer.worker_name}</strong> accept the terms of this offer letter 
                and agree to join NC Help Hub at a rate of <strong>${offer.hourly_rate?.toFixed(2)}/hr</strong>.
              </p>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowSignatureModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSign}
                className="btn-primary flex-1"
                disabled={signing}
                data-testid="confirm-sign-btn"
              >
                {signing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Sign & Accept
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Agreement Detail Modal */}
      {showAgreementDetail && (
        <div className="modal-overlay" onClick={() => setShowAgreementDetail(null)}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="modal-content max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {showAgreementDetail === 'contractor' && (
              <>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <Scale className="w-6 h-6 text-[var(--primary)]" />
                  Employment Agreement
                </h2>
                <div className="prose prose-sm max-w-none">
                  <p className="font-semibold">EMPLOYMENT AGREEMENT</p>
                  <p>This Agreement establishes that the relationship between NC Help Hub ("Company") and the undersigned ("Employee") is that of employer-employee.</p>
                  
                  <p className="font-semibold mt-4">1. W-2 EMPLOYEE STATUS</p>
                  <p>Employee acknowledges and agrees that they are a W-2 employee of the Company. Employee will receive a W-2 tax form at the end of each year for tax filing purposes.</p>
                  
                  <p className="font-semibold mt-4">2. TAX WITHHOLDING</p>
                  <p>The Company will withhold applicable taxes from your paychecks including:</p>
                  <ul>
                    <li>Federal income tax (based on your W-4 form)</li>
                    <li>State income tax (where applicable)</li>
                    <li>Social Security tax (FICA)</li>
                    <li>Medicare tax</li>
                  </ul>
                  
                  <p className="font-semibold mt-4">3. REQUIRED FORMS</p>
                  <p>Employee agrees to complete and submit:</p>
                  <ul>
                    <li>W-4 Employee's Withholding Certificate</li>
                    <li>I-9 Employment Eligibility Verification</li>
                    <li>State tax withholding forms (where applicable)</li>
                  </ul>
                  
                  <p className="font-semibold mt-4">4. WORK ARRANGEMENT</p>
                  <p>Employee agrees to perform services as assigned by the Company. Work schedules and assignments will be coordinated through the NC Help Hub platform.</p>
                </div>
              </>
            )}

            {showAgreementDetail === 'non-solicitation' && (
              <>
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <UserX className="w-6 h-6 text-amber-600" />
                  Non-Solicitation Agreement
                </h2>
                <div className="prose prose-sm max-w-none">
                  <div className="bg-[var(--warning)]/10 border border-[var(--warning)]/30 p-4 rounded-lg mb-4">
                    <p className="text-amber-800 font-semibold">⚠️ IMPORTANT: Please read carefully</p>
                  </div>
                  
                  <p className="font-semibold">NON-SOLICITATION AND NON-CIRCUMVENTION AGREEMENT</p>
                  
                  <p className="font-semibold mt-4">1. NON-SOLICITATION OF CUSTOMERS</p>
                  <p>During the term of this Agreement and for a period of two (2) years following termination, Contractor agrees NOT to:</p>
                  <ul>
                    <li>Directly or indirectly solicit, contact, or provide services to any Customer of NC Help Hub outside of the NC Help Hub platform</li>
                    <li>Accept direct payment from Customers for services that should be booked through NC Help Hub</li>
                    <li>Provide personal contact information to Customers for the purpose of arranging private work</li>
                    <li>Encourage Customers to leave the NC Help Hub platform</li>
                  </ul>
                  
                  <p className="font-semibold mt-4">2. NON-SOLICITATION OF WORKERS</p>
                  <p>Contractor agrees NOT to:</p>
                  <ul>
                    <li>Recruit or solicit other NC Help Hub workers for private work arrangements</li>
                    <li>Encourage other workers to leave the platform</li>
                    <li>Share Customer information with non-NC Help Hub workers</li>
                  </ul>
                  
                  <p className="font-semibold mt-4">3. PENALTIES FOR VIOLATION</p>
                  <div className="bg-[var(--error)]/10 border border-[var(--error)]/30 p-4 rounded-lg">
                    <p className="text-[var(--error)]">Violation of this agreement may result in:</p>
                    <ul className="text-red-700">
                      <li>Immediate termination from the NC Help Hub platform</li>
                      <li>Forfeiture of any pending payments</li>
                      <li>Legal action for damages and injunctive relief</li>
                      <li>Liquidated damages of $5,000 per violation</li>
                    </ul>
                  </div>
                  
                  <p className="font-semibold mt-4">4. ACKNOWLEDGMENT</p>
                  <p>By signing, I acknowledge that I have read, understand, and agree to abide by all terms of this Non-Solicitation Agreement.</p>
                </div>
              </>
            )}

            <div className="mt-6">
              <Button onClick={() => setShowAgreementDetail(null)} className="btn-primary w-full">
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default OfferLetter;
